import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const OptionsManagementTab = () => {
  const [activeSection, setActiveSection] = useState('appliances');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);

  const [optionsData, setOptionsData] = useState({
    appliances: [
      { id: 1, name: 'Plaque de cuisson', enabled: true, order: 1 },
      { id: 2, name: 'Four', enabled: true, order: 2 },
      { id: 3, name: 'Micro-ondes', enabled: true, order: 3 },
      { id: 4, name: 'Lave-vaisselle', enabled: true, order: 4 },
      { id: 5, name: 'Lave-linge', enabled: false, order: 5 },
      { id: 6, name: 'Sèche-linge', enabled: false, order: 6 },
      { id: 7, name: 'Réfrigérateur', enabled: true, order: 7 },
      { id: 8, name: 'Congélateur', enabled: true, order: 8 },
      { id: 9, name: 'Hotte aspirante', enabled: true, order: 9 }
    ],
    finishingElements: [
      { id: 1, name: 'Montants', enabled: true, order: 1 },
      { id: 2, name: 'Fillers', enabled: true, order: 2 },
      { id: 3, name: 'Panneaux latéraux', enabled: true, order: 3 },
      { id: 4, name: 'Cornières', enabled: false, order: 4 },
      { id: 5, name: 'Moulures décoratives', enabled: false, order: 5 }
    ],
    kitchenStyles: [
      { id: 1, name: 'Style simple', enabled: true, order: 1 },
      { id: 2, name: 'Style sans poignées', enabled: true, order: 2 },
      { id: 3, name: 'Style moderne', enabled: false, order: 3 },
      { id: 4, name: 'Style classique', enabled: false, order: 4 }
    ]
  });

  const sectionConfig = {
    appliances: {
      title: 'Électroménager',
      description: 'Gérer les options d\'intégration d\'électroménager disponibles',
      icon: 'Zap'
    },
    finishingElements: {
      title: 'Éléments de finition',
      description: 'Configurer les éléments de finition proposés aux clients',
      icon: 'Package'
    },
    kitchenStyles: {
      title: 'Styles de cuisine',
      description: 'Définir les styles de cuisine disponibles pour les devis',
      icon: 'Palette'
    }
  };

  const handleToggleItem = (section, id) => {
    setOptionsData(prev => ({
      ...prev,
      [section]: prev?.[section]?.map(item =>
        item?.id === id ? { ...item, enabled: !item?.enabled } : item
      )
    }));
  };

  const handleDeleteItem = (section, id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      setOptionsData(prev => ({
        ...prev,
        [section]: prev?.[section]?.filter(item => item?.id !== id)
      }));
    }
  };

  const handleAddItem = () => {
    if (!newItemName?.trim()) return;

    const newItem = {
      id: Date.now(),
      name: newItemName?.trim(),
      enabled: true,
      order: optionsData?.[activeSection]?.length + 1
    };

    setOptionsData(prev => ({
      ...prev,
      [activeSection]: [...prev?.[activeSection], newItem]
    }));

    setNewItemName('');
    setShowAddModal(false);
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetItem) => {
    e?.preventDefault();
    if (!draggedItem || draggedItem?.id === targetItem?.id) return;

    const items = [...optionsData?.[activeSection]];
    const draggedIndex = items?.findIndex(item => item?.id === draggedItem?.id);
    const targetIndex = items?.findIndex(item => item?.id === targetItem?.id);

    // Remove dragged item and insert at target position
    const [removed] = items?.splice(draggedIndex, 1);
    items?.splice(targetIndex, 0, removed);

    // Update order values
    const reorderedItems = items?.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    setOptionsData(prev => ({
      ...prev,
      [activeSection]: reorderedItems
    }));

    setDraggedItem(null);
  };

  const currentItems = optionsData?.[activeSection]?.sort((a, b) => a?.order - b?.order);
  const config = sectionConfig?.[activeSection];

  return (
    <div className="space-y-6">
      {/* Section Selector */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Catégories d'options</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(sectionConfig)?.map(([key, section]) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`p-4 rounded-lg border transition-smooth text-left ${
                activeSection === key
                  ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <Icon name={section?.icon} size={20} />
                <span className="font-medium">{section?.title}</span>
              </div>
              <p className="text-sm opacity-80">{section?.description}</p>
            </button>
          ))}
        </div>
      </div>
      {/* Options Management */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name={config?.icon} size={24} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-text-primary">{config?.title}</h3>
              <p className="text-sm text-text-secondary">{config?.description}</p>
            </div>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            onClick={() => setShowAddModal(true)}
          >
            Ajouter
          </Button>
        </div>

        {/* Items List */}
        <div className="space-y-2">
          {currentItems?.map((item) => (
            <div
              key={item?.id}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, item)}
              className={`flex items-center justify-between p-4 border border-border rounded-lg cursor-move hover:bg-muted/50 transition-smooth ${
                item?.enabled ? 'bg-surface' : 'bg-muted/30'
              }`}
            >
              <div className="flex items-center space-x-4">
                <Icon name="GripVertical" size={16} className="text-text-secondary" />
                <div className="flex items-center space-x-3">
                  <span className={`font-medium ${
                    item?.enabled ? 'text-text-primary' : 'text-text-secondary'
                  }`}>
                    {item?.name}
                  </span>
                  {!item?.enabled && (
                    <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full">
                      Désactivé
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName={item?.enabled ? "Eye" : "EyeOff"}
                  iconSize={16}
                  onClick={() => handleToggleItem(activeSection, item?.id)}
                  className={item?.enabled ? "text-success" : "text-text-secondary"}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Edit2"
                  iconSize={16}
                  className="text-text-secondary hover:text-text-primary"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  iconSize={16}
                  onClick={() => handleDeleteItem(activeSection, item?.id)}
                  className="text-error hover:text-error"
                />
              </div>
            </div>
          ))}

          {currentItems?.length === 0 && (
            <div className="text-center py-12 text-text-secondary">
              <Icon name="Package" size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucun élément configuré</p>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                onClick={() => setShowAddModal(true)}
                className="mt-4"
              >
                Ajouter le premier élément
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">
                Ajouter un élément - {config?.title}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setShowAddModal(false)}
              />
            </div>

            <div className="space-y-4">
              <Input
                label="Nom de l'élément"
                placeholder="Saisir le nom..."
                value={newItemName}
                onChange={(e) => setNewItemName(e?.target?.value)}
                required
              />

              <div className="flex items-center justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="default"
                  onClick={handleAddItem}
                  disabled={!newItemName?.trim()}
                >
                  Ajouter
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptionsManagementTab;